<template>
  <div class="ts-space-y-2">
    <!-- Customer — full width -->
    <div class="row">
      <div class="col-md-12">
        <label class="ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.customer') }}</label>
        <a-select v-model="model.customer_id" :options="customerProfiles" show-search option-filter-prop="children"
          allow-clear class="ts-w-full" :class="{ 'is-invalid': errors.has('customer_id') }"
          placeholder="Select (leave blank to create a Pending card)"></a-select>
        <div class="validate" v-if="errors.has('customer_id')">{{ errors.first('customer_id') }}</div>
      </div>
    </div>

    <!-- Serial Card | Promotion Type -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.serialCard') }}</label>
        <input v-model.trim="model.serial_card" type="text" class="form-control"
          :class="{ 'is-invalid': errors.has('serial_card') }" style="height: 32px;" />
        <div class="validate" v-if="errors.has('serial_card')">{{ errors.first('serial_card') }}</div>
      </div>
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.promotionType') }}</label>
        <a-select v-model="model.promotion_type_id" :options="promotionTypeList" class="ts-w-full"
          :class="{ 'is-invalid': errors.has('promotion_type_id') }" placeholder="Select"
          @change="onPromotionTypeChange"></a-select>
        <div class="validate" v-if="errors.has('promotion_type_id')">{{ errors.first('promotion_type_id') }}</div>
      </div>
    </div>

    <!-- Max Times | Status -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.maxTime') }}</label>
        <input v-model.number="model.max_times" type="number" class="form-control" style="height: 32px;" />
      </div>
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.status') }}</label>
        <a-select v-model="model.status_id" :options="statusList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('status_id') }" placeholder="Select"></a-select>
        <div class="validate" v-if="errors.has('status_id')">{{ errors.first('status_id') }}</div>
      </div>
    </div>

    <!-- Activate Date | Expiry Date -->
    <div class="row">
      <div class="col-md-6">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.activateDate') }}</label>
        <a-date-picker v-model="model.activate_date" class="ts-w-full" show-time format="DD-MM-YYYY HH:mm:ss"
          valueFormat="DD-MM-YYYY HH:mm:ss" :class="{ 'is-invalid': errors.has('activate_date') }"></a-date-picker>
        <div class="validate" v-if="errors.has('activate_date')">{{ errors.first('activate_date') }}</div>
      </div>
      <div class="col-md-6">
        <label class="ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.expiryDate') }}</label>
        <a-date-picker v-model="model.expiry_date" class="ts-w-full" show-time format="DD-MM-YYYY HH:mm:ss"
          valueFormat="DD-MM-YYYY HH:mm:ss" :class="{ 'is-invalid': errors.has('expiry_date') }"></a-date-picker>
        <div class="validate" v-if="errors.has('expiry_date')">{{ errors.first('expiry_date') }}</div>
      </div>
    </div>

    <!-- Card Type -->
    <div class="row">
      <div class="col-md-12">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.cardType') }}</label>
        <a-select v-model="model.card_type_id" :options="cardTypeList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('card_type_id') }" placeholder="Select"
          @change="onCardTypeChange"></a-select>
        <div class="validate" v-if="errors.has('card_type_id')">{{ errors.first('card_type_id') }}</div>
      </div>
    </div>

    <!-- Promotion Package — select + detail card -->
    <div class="row" v-if="model.promotion_type_id === 2">
      <div class="col-md-12">
        <label class="required ts-mb-0 ts-text-xs">{{ $t('customerMemberCard.promotion') }}</label>
        <a-select v-model="model.package_id" :options="promotionList" show-search option-filter-prop="children"
          class="ts-w-full" :class="{ 'is-invalid': errors.has('package_id') }" placeholder="Select" allow-clear
          @change="onPackageChange"></a-select>
        <div class="validate" v-if="errors.has('package_id')">{{ errors.first('package_id') }}</div>
      </div>

      <!-- Promotion Package detail card -->
      <div class="col-md-12 ts-mt-2" v-if="selectedPackage">
        <div class="pkg-record">
          <div class="pkg-header ts-flex ts-items-center ts-justify-between">
            <h4 class="ts-text-sm ts-font-semibold ts-text-gray-700 ts-m-0 ts-flex ts-items-center">
              <span class="ts-inline-flex ts-items-center ts-justify-center ts-rounded ts-mr-2"
                style="width:24px;height:24px;background:#e6f4ff;">
                <i class="fa fa-gift" style="color:#1890ff;font-size:11px;"></i>
              </span>
              <span>
                <span class="ts-block">{{ selectedPackage.package_name }}</span>
                <span class="mc-pkg-description">{{ packageDescriptionText }}</span>
              </span>
            </h4>
            <span class="mc-badge mc-badge-gray">
              {{ $t('customerMemberCard.items') }}: {{ selectedPackage.details.length }}
            </span>
          </div>
          <div class="ts-p-3">
            <div class="table-ui-detail">
              <table>
                <thead>
                  <tr>
                    <th width="3%" class="ts-text-center">{{ $t('no') }}</th>
                    <th width="10%">{{ $t('promotionPackage.barcode') }}</th>
                    <th>{{ $t('promotionPackage.product') }}</th>
                    <th width="8%">{{ $t('promotionPackage.uom') }}</th>
                    <th width="8%" class="ts-text-right">{{ $t('promotionPackage.quantity') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.unitPrice') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.discount') }}</th>
                    <th width="12%" class="ts-text-right">{{ $t('promotionPackage.total') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, i) in selectedPackage.details" :key="d.package_detail_id">
                    <td class="ts-text-center ts-whitespace-no-wrap">{{ i + 1 }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.barcode }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.product_name_en }}</td>
                    <td class="ts-whitespace-no-wrap">{{ d.uom_name_en }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.quantity }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.unit_price }}</td>
                    <td class="ts-text-right ts-whitespace-no-wrap">
                      <span class="mc-pill mc-pill-green" v-if="d.discount_rate">{{ d.discount_rate }}%</span>
                      <span class="mc-pill mc-pill-green" v-else-if="d.discount_amount">{{ d.discount_amount }}</span>
                      <span v-else class="ts-text-gray-400">-</span>
                    </td>
                    <td class="ts-text-right ts-whitespace-no-wrap">{{ d.grand_total }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mc-summary">
              <div class="mc-summary-note">
                <i class="fa fa-info-circle ts-text-blue-400"></i>
                <div>
                  <div class="mc-summary-note-title">
                    {{ $t('customerMemberCard.totalItems') }}:
                    <b>{{ selectedPackage.details.length }}</b>
                    {{ $t('customerMemberCard.activeLines') }}
                  </div>
                  <div class="mc-summary-note-sub">{{ $t('customerMemberCard.quotaNote') }}</div>
                </div>
              </div>

              <div class="mc-summary-figures">
                <div class="mc-summary-line">
                  <span>{{ $t('promotionPackage.total') }} :</span>
                  <span class="mc-input-currency">
                    <span class="mc-input-currency-glyph">$</span>
                    <input type="number" class="form-control mc-input mc-input-readonly"
                      :value="selectedPackage.sub_total" readonly style="height: 24px; width: 90px; text-align: right;" />
                  </span>
                </div>
                <div class="mc-summary-line">
                  <span>{{ $t('customerMemberCard.maxTime') }} :</span>
                  <input type="number" class="form-control mc-input mc-input-readonly" :value="model.max_times" readonly
                    style="height: 24px; width: 90px; text-align: right;" />
                </div>
                <div class="mc-summary-line">
                  <span>{{ $t('promotionPackage.subTotal') }} :</span>
                  <span class="mc-input-currency">
                    <span class="mc-input-currency-glyph">$</span>
                    <input type="number" class="form-control mc-input mc-input-readonly" :value="packageSubTotal"
                      readonly style="height: 24px; width: 90px; text-align: right;" />
                  </span>
                </div>
                <div class="mc-summary-line">
                  <span>{{ $t('promotionPackage.discount') }} :</span>
                  <span class="mc-summary-discount-input">
                    <span class="mc-segmented">
                      <button type="button" class="mc-segmented-btn"
                        :class="{ 'mc-segmented-btn-active': model.discount_type === '$' }"
                        @click.prevent="model.discount_type = '$'">$</button>
                      <button type="button" class="mc-segmented-btn"
                        :class="{ 'mc-segmented-btn-active': model.discount_type === '%' }"
                        @click.prevent="model.discount_type = '%'">%</button>
                    </span>
                    <input v-model.number="model.discount_rate" type="number" min="0" class="form-control mc-input"
                      style="height: 24px; width: 90px; text-align: right;" />
                  </span>
                </div>
                <div class="mc-summary-line mc-summary-line-total">
                  <span>{{ $t('customerMemberCard.grandTotal') }} :</span>
                  <span class="mc-summary-grand-total">$ {{ packageGrandTotal }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2 ts-mt-3">
        <ts-button :disabled="loading" @click.prevent="$emit('cancel')">
          {{ $t('cancel') }}
        </ts-button>
        <ts-button v-if="!isUpdate" color="primary" @click.prevent="onCheckExist" :disabled="loading">
          {{ $t('save') }}
        </ts-button>
        <ts-button v-if="isUpdate" color="primary" @click.prevent="onUpdate" :disabled="loading">
          {{ $t('update') }}
        </ts-button>
      </div>
    </div>
    <Spin size="large" fix v-if="loading"></Spin>
    <!-- form-action -->
    <a-modal v-if="exist" v-model="exist" centered :header="null" :zIndex="1050" :footer="null" :closable="false"
      width="400px">
      <div class="ts-space-y-2">
        <div class="row">
          <div class="ts-flex ts-items-center ts-gap-3 ts-px-4 ts-pb-3 ts-border-b ts-border-gray-200 ts-w-full">
            <div class="ts-flex-shrink-0">
              <i class="fas fa-exclamation ts-text-orange-500"></i>
            </div>
            <b class="ts-text-sm ts-font-medium ts-text-gray-900">
              Are you sure ?
            </b>
          </div>

          <div class="ts-px-2 ts-pt-2 ts-pb-1 ts-w-full">
            <span class="ts-text-sm ts-text-gray-600">
              This customer is already activated with other serial card. Do you
              want to overwirte it?
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required ts-mb-0 ts-text-xs">{{
              $t('customerMemberCard.remark')
            }}</label>
            <textarea v-model="model.remark" rows="3" class="form-control"
              :class="{ 'is-invalid': errors.has('remark') }" placeholder="Note something here"></textarea>
            <div class="validate" v-if="errors.has('remark')">
              {{ errors.first('remark') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-2 ts-mt-3">
            <ts-button :disabled="loading" @click.prevent="$emit('cancel')">
              {{ $t('cancel') }}
            </ts-button>

            <ts-button v-if="!isUpdate" :disabled="loading" @click.prevent="onConfirm" outline color="primary">
              {{ $t('confirm') }}
            </ts-button>
          </div>
        </div>
        <Spin size="large" fix v-if="loading"></Spin>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'customerMemberCard',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      exist: false,
      model: {
        customer_member_card_id: null,
        customer_id: null,
        serial_card: null,
        promotion_type_id: 1,
        package_id: null,
        activate_date: moment().format('DD-MM-YYYY HH:mm:ss'),
        expiry_date: null,
        status_id: 3,
        remark: null,
        overwirte: false,
        max_times: null,
        card_type_id: null,
        discount_type: '%',
        discount_rate: 0
      }
    }
  },
  created() {
    this.setEditData()
    this.fetchData()
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['edit_data', 'formViewData']),
    isUpdate() {
      return !_.isEmpty(this.edit_data)
    },
    customerProfiles() {
      if (this.formViewData.customer && this.formViewData.customer.length) {
        return this.formViewData.customer.map(el => ({
          label:
            'Code: ' +
            el.customer_code +
            ' | Customer: ' +
            el.customer_name +
            ' | Contact: ' +
            el.contact_number +
            ' | Gender: ' +
            el.gender,
          value: el.customer_id
        }))
      }
      return []
    },
    cardTypeList() {
      if (this.formViewData.cardType && this.formViewData.cardType.length) {
        return this.formViewData.cardType.map(el => ({
          label: el.card_types,
          value: el.card_type_id,
          max_times: el.max_times
        }))
      }
      return []
    },
    promotionList() {
      if (this.formViewData.promotions && this.formViewData.promotions.length) {
        return this.formViewData.promotions.map(el => ({
          label: el.package_name,
          value: el.package_id
        }))
      }
      return []
    },
    selectedPackage() {
      if (!this.model.package_id || !this.formViewData.promotions) return null
      return this.formViewData.promotions.find(p => p.package_id === this.model.package_id) || null
    },
    packageDescriptionText() {
      if (!this.selectedPackage) return ''
      const count = this.selectedPackage.details.length
      return `${this.$t('customerMemberCard.packageIncludes')} ${count} ${this.$t('customerMemberCard.bundledItems')}`
    },
    packageSubTotal() {
      if (!this.selectedPackage) return 0
      return this.selectedPackage.sub_total * (this.model.max_times || 0)
    },
    packageDiscountAmount() {
      const subTotal = this.packageSubTotal
      const rate = this.model.discount_rate || 0
      const amount = this.model.discount_type === '$' ? rate : (subTotal * rate) / 100
      return Math.min(Math.max(amount, 0), subTotal)
    },
    packageGrandTotal() {
      return this.packageSubTotal - this.packageDiscountAmount
    },
    statusList() {
      return [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 },
        { label: 'Pending', value: 3 }
      ]
    },
    promotionTypeList() {
      return [
        { label: 'Normal', value: 1 },
        { label: 'Package', value: 2 }
      ]
    }
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    async fetchData() {
      this.loading = true
      await this.getFormViewData({
        params: undefined,
        index: undefined
      }).finally(() => (this.loading = false))
    },
    async onCheckExist() {
      this.loading = true
      await this.$store
      this.$store
        .dispatch('customer/customerMemberCard/checkExist', this.model)
        .then(response => {
          this.exist = response.exist
          if (response.exist) {
            this.loading = false
          } else {
            this.onSave()
          }
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    onConfirm() {
      this.model.overwirte = true
      this.onSave()
    },
    onPackageChange(selectedId) {
      if (!selectedId) this.model.package_id = null
    },
    onPromotionTypeChange(value) {
      if (value === 1) {
        this.model.package_id = null
        this.model.discount_type = '%'
        this.model.discount_rate = 0
      }
    },
    onCardTypeChange(selectedId) {
      const selectedCard = this.cardTypeList.find(c => c.value === selectedId)
      this.model.max_times = selectedCard ? selectedCard.max_times : null
    },
    onUpdate() {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/update', {
          id: this.model.customer_member_card_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onSave() {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.customer_member_card_id = this.edit_data.customer_member_card_id
        this.model.customer_id = this.edit_data.customer_id
        this.model.serial_card = this.edit_data.serial_card
        this.model.card_type_id = this.edit_data.card_type_id
        this.model.max_times = this.edit_data.max_times
        this.model.promotion_type_id = this.edit_data.promotion_type_id ? this.edit_data.promotion_type_id : 1
        this.model.package_id = this.edit_data.package_id
        this.model.activate_date = this.edit_data.activate_date
          ? moment(
            this.edit_data.activate_date,
            'DD-MM-YYYY hh:mm:ss A'
          ).format('DD-MM-YYYY HH:mm:ss')
          : null
        this.model.expiry_date = this.edit_data.expiry_date
          ? moment(this.edit_data.expiry_date, 'DD-MM-YYYY hh:mm:ss A').format(
            'DD-MM-YYYY HH:mm:ss'
          )
          : null
        this.model.status_id = this.edit_data.status_id
        this.model.remark = this.edit_data.remark
        this.model.discount_type = this.edit_data.discount_type || '%'
        this.model.discount_rate = this.edit_data.discount_rate || 0
      }
    },
    clearInput() {
      this.model.customer_member_card_id = null
      this.model.customer_id = null
      this.model.serial_card = null
      this.model.activate_date = moment().format('DD-MM-YYYY HH:mm:ss')
      this.model.expiry_date = null
      this.model.status_id = 3
      this.model.remark = null
      this.model.overwirte = false
      this.model.card_type_id = null
      this.model.promotion_type_id = 1
      this.model.package_id = null
      this.model.discount_type = '%'
      this.model.discount_rate = 0
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {})
    }
  },
  watch: {
    'model.customer_id'(value) {
      if (!value) {
        this.model.status_id = 3
      } else if (this.model.status_id === 3) {
        this.model.status_id = 1
      }
    }
  }
}
</script>
<style>
.ant-calendar-picker-input {
  height: 32px !important;
}

.pkg-record {
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
}

.pkg-header {
  padding: 10px 16px;
  background: #fafafa;
}
</style>
<style scoped>
/* Package badge */
.mc-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  white-space: nowrap;
}

.mc-badge-gray {
  background: #f3f4f6;
  color: #4b5563;
}

/* Package summary */
.mc-pkg-description {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: #9ca3af;
}

.mc-pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
}

.mc-pill-green {
  background: #dcfce7;
  color: #15803d;
}

.mc-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e5e7eb;
  flex-wrap: wrap;
}

.mc-summary-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 260px;
}

.mc-summary-note-title {
  font-size: 12px;
  color: #374151;
}

.mc-summary-note-sub {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.mc-summary-figures {
  min-width: 260px;
  flex: 1;
  max-width: 320px;
}

.mc-summary-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #4b5563;
  padding: 3px 0;
}

.mc-input {
  padding: 1px 6px;
  font-size: 12px;
}

.mc-input-currency {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.mc-input-currency-glyph {
  position: absolute;
  left: 6px;
  font-size: 10px;
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
}

.mc-input-currency input {
  padding-left: 16px;
}

.mc-input-readonly {
  background: #f3f4f6;
  color: #1f2937;
  font-weight: 600;
  cursor: default;
}

.mc-summary-discount-input {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mc-summary-line-total {
  margin-top: 6px;
  padding: 5px 12px 5px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-weight: 700;
  color: #1d4ed8;
}

.mc-summary-grand-total {
  padding-right: 15px;
  font-weight: 700;
  color: #1d4ed8;
}

/* Segmented control */
.mc-segmented {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
}

.mc-segmented-btn {
  border: none;
  background: #fff;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  cursor: pointer;
}

.mc-segmented-btn+.mc-segmented-btn {
  border-left: 1px solid #d1d5db;
}

.mc-segmented-btn-active {
  background: #2563eb;
  color: #fff;
}
</style>
